import { BRAND_FAN_URL } from "@/lib/brandConfig";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";

/**
 * IMPORTANTE — nomenclatura legacy:
 * - `birthday` (DB / Worker flag `showBirthday`) hoy se usa como **CTA "Comunidad"**
 *   apuntando a `/bestfriend-instagram`. Mantenemos la key del Worker para no requerir
 *   cambios en el Cloudflare Worker ni migración de DB.
 * - `community` (DB / Worker flag `showCommunity`) hoy se usa como **CTA "Exclusivo"**
 *   apuntando a `/invitacion-premium`. Mismo motivo.
 *
 * GATE `config.template === "BaseTemplate"`:
 * El Worker emite las flags showInstagram/showWhatsappCommunity/showCommunity/
 * showBirthday/showWishlist SOLO cuando `templateUsesWishlistGeoCta(template)`
 * es true — y por la regla canónica, eso únicamente se cumple para BaseTemplate
 * (ver cloudflare-worker-juaperrone/worker.js:12-26). Otros templates
 * (BioTemplatePremium*, VideoTemplate, etc.) reciben `mode: "default"` aquí
 * porque las flags llegan undefined/false → caller decide su CTA específico.
 */
export type WorkerDrivenCtaVariant =
  | "community"
  | "birthday"
  | "wishlist"
  | "whatsapp"
  | "instagram";

export type WorkerDrivenCtaResolution =
  | { mode: "hidden" }
  | { mode: "worker"; variant: WorkerDrivenCtaVariant; url: string }
  | { mode: "default" };

/**
 * URL del CTA "Comunidad" (variant `birthday`). Default: `/bestfriend-instagram` (página interna).
 * Override por brand vía Railway: `VITE_BESTFRIEND_INSTAGRAM_CTA_URL` (puede ser absoluta o ruta).
 */
const COMUNIDAD_CTA_URL =
  (import.meta.env.VITE_BESTFRIEND_INSTAGRAM_CTA_URL as string | undefined)?.trim() ||
  "/bestfriend-instagram";

/**
 * CTA "Mejores Amigos de Instagram" (variant `instagram`).
 * Stripe-only (MercadoPago retirado): landing interna que abre
 * `/api/create-subscription-checkout` con plan_tier=wp.
 */
const INSTAGRAM_MEJORES_AMIGOS_CTA_URL = "/bestfriend-instagram";
/** CTA "Comunidad de WhatsApp" (variant `whatsapp`). Mismo destino que BaseTemplate. */
const WHATSAPP_COMMUNITY_CTA_URL = "/whatsapp-broadcast";

/**
 * Misma prioridad que el CTA del hero en BaseTemplate(Black):
 * hideCta → sin CTA; si no, showInstagram → /bestfriend-instagram (Stripe "Mejores Amigos");
 * showWhatsappCommunity → /whatsapp-broadcast ("Comunidad WhatsApp");
 * showCommunity → /invitacion-premium ("Exclusivo");
 * showBirthday → /bestfriend-instagram ("Comunidad"); showWishlist → fan/wishlist;
 * si no hay flags, default (checkout / VIP lo define el caller).
 * El Worker emite exactamente una flag, así que el orden solo desempata.
 */
export function resolveWorkerDrivenCta(): WorkerDrivenCtaResolution {
  const cfg = getServerTemplateConfig();
  if (!cfg) return { mode: "default" };
  if (cfg.hideCta) return { mode: "hidden" };
  if (cfg.showInstagram) {
    return {
      mode: "worker",
      variant: "instagram",
      url: INSTAGRAM_MEJORES_AMIGOS_CTA_URL,
    };
  }
  if (cfg.showWhatsappCommunity) {
    return {
      mode: "worker",
      variant: "whatsapp",
      url: WHATSAPP_COMMUNITY_CTA_URL,
    };
  }
  if (cfg.showCommunity) {
    return {
      mode: "worker",
      variant: "community",
      url: "/invitacion-premium",
    };
  }
  if (cfg.showBirthday) {
    return {
      mode: "worker",
      variant: "birthday",
      url: COMUNIDAD_CTA_URL,
    };
  }
  if (cfg.showWishlist) {
    return {
      mode: "worker",
      variant: "wishlist",
      url: `${BRAND_FAN_URL}/wishlist`,
    };
  }
  return { mode: "default" };
}

type Lang = "en" | "es" | "de" | "fr" | "it" | "pt" | "nl";

const WORKER_CTA_LABELS: Record<
  WorkerDrivenCtaVariant,
  Record<Lang, string>
> = {
  // `community` slot → ahora etiquetado como "Exclusivo" (apunta a /invitacion-premium).
  community: {
    es: "Exclusivo",
    en: "Exclusive",
    pt: "Exclusivo",
    fr: "Exclusif",
    de: "Exklusiv",
    it: "Esclusivo",
    nl: "Exclusief",
  },
  // `birthday` slot → ahora etiquetado como "Comunidad" (apunta a /bestfriend-instagram).
  birthday: {
    es: "Comunidad",
    en: "Community",
    pt: "Comunidade",
    fr: "Communauté",
    de: "Community",
    it: "Community",
    nl: "Community",
  },
  wishlist: {
    es: "Ingresa a mi wishlist de cumpleaños",
    en: "Open my birthday wishlist",
    pt: "Ver minha lista de presentes",
    fr: "Voir ma wishlist d'anniversaire",
    de: "Zur Geburtstags-Wishlist",
    it: "Apri la mia wishlist di compleanno",
    nl: "Bekijk mijn verlanglijst",
  },
  // `instagram` → checkout de Mercado Pago.
  instagram: {
    es: "Mejores Amigos",
    en: "Close Friends",
    pt: "Melhores Amigos",
    fr: "Amis proches",
    de: "Enge Freunde",
    it: "Amici stretti",
    nl: "Beste vrienden",
  },
  // `whatsapp` → /whatsapp-broadcast.
  whatsapp: {
    es: "Unite a mi comunidad",
    en: "Join my community",
    pt: "Entre na minha comunidade",
    fr: "Rejoins ma communauté",
    de: "Tritt meiner Community bei",
    it: "Unisciti alla community",
    nl: "Word lid van mijn community",
  },
};

export function getWorkerCtaDisplayLabel(
  language: string | undefined,
  variant: WorkerDrivenCtaVariant,
): string {
  const key = (language?.split("-")[0]?.toLowerCase() as Lang) || "en";
  const row = WORKER_CTA_LABELS[variant];
  return row[key] ?? row.en;
}
